import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import { Box, createStyles, makeStyles, Theme } from "@material-ui/core";
import { AxiosResponse } from "axios";
import { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomTextField from "../../../components/Input/CustomTextField";
import { tesaDarkRedError } from "../../../styles/colors";
import ProductApi, { TesaPrototypeRequest } from "../../../api/services/ProductApi";
import { Laboratory } from "../../../api/models/product/Laboratory";
import { TesaPrototype } from "../../../api/models/product/TesaPrototype";
import useEnv from "../../../hooks/env";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import PagedResponse from "../../../api/models/PagedResponse";
import {
  LaboratoriesSearchAutocomplete,
  NartsSearchAutocomplete,
} from "../../../components/Autocomplete/CustomSearchAutoComplete.instances";
import ProductEditCreateDialog from "../ProductEditCreateDialog";
import ProductEditCreateButtons from "../ProductEditCreateButtons";
import NartResponse from "../../../api/models/product/NartResponse";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    invalidProdName: {
      color: tesaDarkRedError,
      fontSize: "0.75rem",
      marginLeft: "14px",
      marginTop: "3px",
    },
    marginBottom: {
      marginBottom: theme.spacing(4),
    },
    marginTop: {
      marginTop: theme.spacing(5),
    },
    marginRight: {
      marginRight: theme.spacing(3),
    },
  }),
);

interface PrototypeDialogForm {
  responsibleLaboratory: Laboratory | null;
  name: string;
  responsibleDeveloper: string;
  referringTesaProductNart: NartResponse;
  description: string;
}

interface PrototypeEditCreateDialogProps extends CustomDialogProps {
  initialData?: TesaPrototype;
  readonly?: boolean;
  openCreateLaboratoryDialog: () => void;
  exitedCallback?: () => void;
}

const PrototypeEditCreateDialog = (props: PrototypeEditCreateDialogProps) => {
  const { api } = useEnv();
  const productApi = new ProductApi(api);

  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);

  const { initialData, readonly, exitedCallback, openCreateLaboratoryDialog, open, onClose } = props;

  const laboratoriesSearch = useRef<HTMLDivElement & { reset: () => void }>(null);
  const productsSearch = useRef<HTMLDivElement & { reset: () => void }>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [invalidPrototypeName, setInvalidPrototypeName] = useState(false);
  const [responsibleLaboratoryId, setResponsibleLaboratoryId] = useState<number | null>(null);

  const classes = useStyles();

  const isCreate = !initialData;
  useEffect(() => {
    const interval = setTimeout(() => {
      if (searchTerm && responsibleLaboratoryId) {
        productApi
          .validatePrototypeName(responsibleLaboratoryId, searchTerm)
          .then((res: AxiosResponse<boolean>) => setInvalidPrototypeName(res.data))
          .catch(handleHttpError);
      }
    }, 300);
    return () => {
      clearInterval(interval);
    };
  }, [searchTerm]);

  const yupValidationSchema = Yup.object().shape({
    responsibleLaboratory: Yup.mixed().required("Please select a laboratory"),
    name: Yup.string().required("Please enter a prototype name"),
    responsibleDeveloper: Yup.string(),
    referringTesaProductNart: Yup.object().nullable(),
    description: Yup.string(),
  });

  const formToPayload = (form: PrototypeDialogForm): TesaPrototypeRequest => {
    const { responsibleLaboratory, referringTesaProductNart, ...rest } = form;
    return {
      ...rest,
      referringTesaProductNart: referringTesaProductNart.nart,
      responsibleLaboratoryId: responsibleLaboratory?.id as number,
    };
  };

  const initializeForm = (productData: TesaPrototype | undefined): PrototypeDialogForm => {
    const referringTesaProductNart = { nart: productData?.referringTesaProductNart || "" };
    return {
      referringTesaProductNart,
      name: productData?.name || "",
      description: productData?.description || "",
      responsibleDeveloper: productData?.responsibleDeveloper || "",
      responsibleLaboratory: productData?.responsibleLaboratory || null,
    };
  };

  const handleSubmit = (form: PrototypeDialogForm, productId?: number) => {
    const payload = formToPayload(form);
    const updateOrCreate = productId
      ? () => productApi.updatePrototype(productId, payload)
      : () => productApi.createNewPrototype(payload);
    updateOrCreate()
      .then(() => {
        setInvalidPrototypeName(false);
        showSnackbar(`Prototype ${productId ? "updated" : "created"}`, "success");
        onClose(true, "prototype");
      })
      .catch(handleHttpError);
  };

  return (
    <Formik<PrototypeDialogForm>
      enableReinitialize
      initialValues={initializeForm(initialData)}
      onSubmit={(values) => {
        handleSubmit(values, initialData?.id);
      }}
      validationSchema={yupValidationSchema}
    >
      {(formikProps: FormikProps<PrototypeDialogForm>): JSX.Element => {
        const { errors, handleBlur, handleChange, isSubmitting, resetForm, touched, values, setFieldValue } =
          formikProps;
        return (
          <ProductEditCreateDialog
            create={isCreate}
            open={open}
            title="prototype"
            handleClose={() => {
              onClose(false, "prototype");
            }}
            handleExited={() => {
              resetForm();
              setInvalidPrototypeName(false);
              exitedCallback?.();
            }}
          >
            <Form>
              <Grid container justify="flex-start" direction="row" spacing={4}>
                <Grid item xs={6}>
                  <LaboratoriesSearchAutocomplete
                    disabled={readonly}
                    autoFocus
                    mandatory
                    ref={laboratoriesSearch}
                    inputLabel="Responsible Laboratory"
                    optionFetch={(query: string): Promise<PagedResponse<TesaPrototype>> => {
                      return productApi
                        .getLaboratories(query)
                        .then((res: AxiosResponse) => {
                          return res?.data;
                        })
                        .catch(handleHttpError);
                    }}
                    helperText={
                      errors.responsibleLaboratory && touched.responsibleLaboratory
                        ? errors.responsibleLaboratory
                        : "Name of the Laboratory incl. BU and region"
                    }
                    onChange={(e, value): void => {
                      setFieldValue("responsibleLaboratory", value);
                      setResponsibleLaboratoryId(value ? value.id : null);
                    }}
                    map={(value): { key: string; label: string } => ({
                      key: `${value.id}`,
                      label: `${value.name}`,
                    })}
                    value={values.responsibleLaboratory}
                    error={!!errors.responsibleLaboratory && !!touched.responsibleLaboratory}
                  />
                  <div className="helperTextLink" role="link" tabIndex={0} onClick={openCreateLaboratoryDialog}>
                    <Box height={16} />
                    Not found? Create new tesa Laboratory
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    mandatory
                    fullWidth
                    disabled={!values.responsibleLaboratory || readonly}
                    label="Prototype Name"
                    name="name"
                    id="name"
                    value={values.name}
                    type="text"
                    helperText={errors.name && touched.name ? errors.name : ""}
                    error={!!(errors.name && touched.name) || invalidPrototypeName}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      handleChange(e);
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (invalidPrototypeName) {
                        setFieldValue("name", "");
                        setInvalidPrototypeName(false);
                      }
                    }}
                  />
                  {invalidPrototypeName && (
                    <div className={classes.invalidProdName}>
                      This product already exists in the tesa Prototype Database. Please use that product or enter a
                      different name.
                    </div>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled={!values.responsibleLaboratory || readonly}
                    fullWidth
                    name="responsibleDeveloper"
                    id="responsibleDeveloper"
                    label="Responsible Developer"
                    value={values.responsibleDeveloper}
                    type="text"
                    helperText={
                      errors.responsibleDeveloper && touched.responsibleDeveloper ? errors.responsibleDeveloper : ""
                    }
                    error={!!(errors.responsibleDeveloper && touched.responsibleDeveloper)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={6}>
                  <NartsSearchAutocomplete
                    ref={productsSearch}
                    disabled={!values.responsibleLaboratory || readonly}
                    error={!!errors.referringTesaProductNart && !!touched.referringTesaProductNart}
                    helperText={
                      errors.referringTesaProductNart && touched.referringTesaProductNart
                        ? (errors.referringTesaProductNart as string)
                        : ""
                    }
                    inputLabel="Referring tesa Product"
                    onChange={(e, value): void => {
                      setFieldValue("referringTesaProductNart", value);
                    }}
                    optionFetch={(searchQuery): Promise<{ totalPages: number; data: NartResponse[] }> => {
                      return productApi.getNarts(searchQuery).then((response) => ({
                        totalPages: 1,
                        data: response.data,
                      }));
                    }}
                    map={(value): { key: string; label: string } => ({
                      key: value.nart,
                      label: value.nart,
                    })}
                    value={values.referringTesaProductNart}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    disabled={!values.responsibleLaboratory || readonly}
                    fullWidth
                    name="description"
                    id="description"
                    label="Description"
                    value={values.description}
                    type="text"
                    helperText={errors.description && touched.description ? errors.description : ""}
                    error={!!(errors.description && touched.description)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>
              <ProductEditCreateButtons
                create={isCreate}
                readonly={readonly}
                disabledConfirm={isSubmitting}
                disabledDismiss={isSubmitting}
                handleDismissClick={() => {
                  onClose(false, "prototype");
                }}
              />
            </Form>
          </ProductEditCreateDialog>
        );
      }}
    </Formik>
  );
};

export default PrototypeEditCreateDialog;
